import { Injectable } from '@angular/core';

import { ServiceBase } from './service.base';
import { catchError, map } from 'rxjs/operators';
import { SupportFile, SupportFiles } from 'app/shared/models/support-file';
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { INTERCEPTInternalServerError } from 'app/core/interceptors/contexts';
import {
  UploadResponse,
  DeleteResponse,
  ProductFile,
  Categories,
  CopyResponse,
} from '../../shared/interfaces/upload-file-support-page';

/**
 * Query parameter carrying the blob path of `DELETE support-files-blob`.
 *
 * @remarks
 * The path must not travel as route segments: the API Management operation
 * imported from the Function App only matches a single segment, so every
 * multi-segment blob path got a 404 from the gateway.
 */
const BLOB_PATH_QUERY_PARAM = 'blobPath';

@Injectable({
  providedIn: 'root',
})
export class SupportPageService extends ServiceBase {
  public supportFileId: string;

  constructor(
    private readonly http: HttpClient,
    private readonly translateService: TranslateService,
  ) {
    super();
  }

  getPresignedUrl(files: Array<string>) {
    return this.http
      .put(
        this.UrlServiceV1 + 'support-files/signedUrl',
        files,
        this.obterAuthHeader(),
      )
      .pipe(
        map(this.extractData<Record<string, any>>),
        catchError(this.serviceError),
      );
  }

  uploadFile(file: File, url: string): Observable<UploadResponse> {
    return this.http
      .put<UploadResponse>(url, file, {
        headers: new HttpHeaders({ 'X-Ms-Blob-Type': 'BlockBlob' }),
      })
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getAllSupportFile() {
    return this.http
      .get(`${this.UrlServiceV1}support-files`, this.obterAuthHeader())
      .pipe(
        map(this.extractData<(ProductFile | Categories)[]>),
        catchError(this.serviceError),
      );
  }

  getSupportFileById(id: string): Observable<ProductFile | Categories> {
    const url = `${this.UrlServiceV1}support-files`;
    const params = new HttpParams().set('id', id);

    return this.http
      .get<ProductFile | Categories>(url, {
        params,
        ...this.obterAuthHeader(),
      })
      .pipe(catchError(this.serviceError));
  }

  postSupportFile(
    supportFileData: ProductFile | Categories,
  ): Observable<UploadResponse> {
    const url = `${this.UrlServiceV1}support-files/upload`;
    return this.http
      .put<UploadResponse>(url, supportFileData, this.obterAuthHeader())
      .pipe(
        catchError((error) => {
          if (error.status === 409) {
            this.translateService
              .get('global.support-duplicate-file')
              .subscribe((message) => {
                alert(message);
              });
          }
          return this.serviceError(error);
        }),
      );
  }

  updateSupportFile(id: string, updatedData: ProductFile | Categories) {
    const url = `${this.UrlServiceV1}support-files/update?id=${id}`;
    return this.http
      .put(url, updatedData, this.obterAuthHeader())
      .pipe(map(this.extractData<SupportFile>), catchError(this.serviceError));
  }

  checkIfExists(
    productId: string,
    categoryName: string,
  ): Observable<ProductFile> {
    const url = `${this.UrlServiceV1}support-files?id=${productId}&category=${categoryName}`;
    return this.http.get<ProductFile>(url, this.obterAuthHeader());
  }

  patchSupportFile(productId: string, data: FormData): Observable<ProductFile> {
    const url = `${this.UrlServiceV1}support-files?id=${productId}`;
    return this.http.patch<ProductFile>(url, data);
  }

  deleteProductFromCosmo(productId: string): Observable<ProductFile> {
    const url = `${this.UrlServiceV1}support-files/${productId}`;
    return this.http.delete<ProductFile>(url, this.obterAuthHeader());
  }

  deleteProductFileFromBlob(
    productId: string,
    productCategory: string,
    filePath: string,
    fileCategory?: string,
    fileVersion?: string,
    fileDate?: string,
  ): Observable<DeleteResponse> {
    let blobPath: string;

    if (filePath.startsWith(productCategory)) {
      blobPath = filePath;
    } else if (productId && fileCategory && fileVersion && fileDate) {
      blobPath = `${productCategory}/${productId}/${fileCategory}/${fileVersion}/${fileDate}/${filePath}`;
    } else {
      blobPath = `${productCategory}/${filePath}`;
    }

    return this.deleteSupportBlob(blobPath);
  }

  /**
   * Deletes a support blob by its full stored path, as-is.
   *
   * @remarks
   * Callers holding `filePathBlobStorage` must use this method instead of
   * {@link deleteProductFileFromBlob}: that one only trusts paths starting with
   * the product's current category, and a product whose category changed after
   * the upload would get a rebuilt path to a blob that does not exist. The
   * deletion is idempotent, so the wrong path would silently "succeed" and
   * orphan the real blob.
   */
  deleteSupportBlob(blobPath: string): Observable<DeleteResponse> {
    // Built by hand instead of `HttpParams`: its codec leaves `+` unencoded,
    // and the Function would read it back as a space.
    const url = `${this.UrlServiceV1}support-files-blob?${BLOB_PATH_QUERY_PARAM}=${encodeURIComponent(blobPath)}`;

    return this.http
      .delete<DeleteResponse>(url, {
        ...this.obterAuthHeader(),
        // Storage failures are reported by the caller's toast instead of the
        // maintenance page.
        context: new HttpContext().set(INTERCEPTInternalServerError, false),
      })
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

copyBlob(sourcePath: string, destinationPath: string): Observable<CopyResponse> {
  const body = {
    sourcePath,
    destinationPath,
  };
  return this.http
    .post<CopyResponse>(
      `${this.UrlServiceV1}support-files-copy`,
      body,
      this.obterAuthHeader(),
    )
    .pipe(map(this.extractData), catchError(this.serviceError));
}
}
